import dayjs from "dayjs"

import SupportTickets from "@/API/backoffice/Tickets.js"

import LoadingMixin from "@/mixins/loading.js"
import SystemMixin from "@/mixins/system.js"
import HandleRequestErrorsMixin from "@/mixins/handle_request_errors.js"

import {storageAvailable} from "@/support/storage.js"
import {filterToQuery} from "@/support/advanced_filter.js"

import {options as StatusOptions} from "@/enums/status.js"

// TODO: Remove vuex-pathify dependency
import {sync} from "vuex-pathify"

// Filters kept in the URL (the advanced filter goes to the storage instead)
const URL_FILTERS = ['status', 'sort', 'page']

// Where the advanced filter survives a refresh / coming back from the detail
const STORAGE_KEY = 'tickets.index.filter'

export default {
    mixins: [
        LoadingMixin,
        SystemMixin,
        HandleRequestErrorsMixin
    ],
    props: {
        filters: Object
    },
    data() {
        return {
            filter: {
                status: null,
                advanced: [],
                sort: null,
                page: 1,
            },
        }
    },
    computed: {
        ...sync('SupportTickets', ['page']),
        filterSelects() {
            return [
                {
                    label: 'Todos status',
                    options: StatusOptions,
                    filter: 'status'
                }
            ]
        },
        actions() {
            return {
                options: [
                    {label: 'Ativar', id: 'ativar'},
                    {label: 'Desativar', id: 'desativar'},
                    {label: 'Excluir', id: 'excluir'},
                    {label: 'Consulta financeiro', id: 'consulta_financeiro'},
                ]
            }
        },
        /**
         * Any filter applied (status does not count, there is always an active tab)
         * @returns {boolean}
         */
        hasSearchFilter() {
            return this.filter.advanced?.length > 0
        }
    },
    methods: {
        /**
         * Advanced filter with the dates in the format the backend compares (day, not timestamp)
         * @returns {Object[]}
         */
        normalizedAdvancedFilter() {
            return (this.filter.advanced ?? []).map(item => {

                if (item.field?.type !== 'date' || blank(item.value)) return item

                return {...item, value: dayjs(item.value).format('YYYY-MM-DD')}
            })
        },
        /**
         * Listing params: no empty values and the advanced filter as a query
         * @returns {Object}
         */
        filterParams() {

            const params = {
                status: this.filter.status,
                sort: this.filter.sort,
                page: this.filter.page,
                filters: filterToQuery(this.normalizedAdvancedFilter())
            }

            return Object.fromEntries(
                Object.entries(params).filter(([, value]) => filled(value))
            )
        },
        /**
         * Action load
         * @param background
         * @returns {Promise<boolean>}
         */
        async load(background = false) {

            const filter = this.filterParams()

            console.debug('SupportTickets/Index@load: Loading data.', {filter, background})

            background ? this.bgLoading() : this.loading()

            const [err, res] = await SupportTickets.list(filter)

            this.loaded()

            if (err) {
                console.error('SupportTickets/Index@load: Error on loading data.', {err})
                return this.handleRequestError(err)
            }

            this.page = res.data

            this.saveFilterStorage()

            console.debug('SupportTickets/Index@load: Data loaded.', {data: res.data})

            return true
        },
        /**
         * Reloads the listing from the first page (tab change, filter, sorting)
         * @returns {Promise<boolean>}
         */
        async reload() {
            this.filter.page = 1

            this.syncUrl()

            return this.load()
        },
        /**
         * Keeps the tab / sorting in the URL to survive a refresh / coming back from the detail
         */
        syncUrl() {

            const query = Object.fromEntries(
                URL_FILTERS.map(key => [key, this.filter[key]]).filter(([, value]) => filled(value))
            )

            if (JSON.stringify(query) === JSON.stringify(this.$route.query)) return

            this.$router.replace({query}).catch(() => {})
        },
        /**
         * Reads the tab / sorting from the URL (keeping the defaults when absent)
         */
        restoreFilterFromUrl() {

            const query = this.$route.query

            URL_FILTERS.forEach(key => {
                if (query[key] === undefined) return

                const value = ['status', 'page'].includes(key)
                    ? Number(query[key])
                    : query[key]

                this.$set(this.filter, key, value)
            })
        },
        /**
         * Stores the advanced filter, too complex to fit in the URL
         * @returns {boolean}
         */
        saveFilterStorage() {

            if (!storageAvailable('localStorage')) return false

            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.filter.advanced ?? []))

            return true
        },
        /**
         * Restores the stored advanced filter
         * @returns {boolean}
         */
        restoreFilterStorage() {

            if (!storageAvailable('localStorage')) return false

            const stored = localStorage.getItem(STORAGE_KEY)

            if (blank(stored)) return false

            try {
                this.$set(this.filter, 'advanced', JSON.parse(stored))
            } catch (err) {
                console.error('SupportTickets/Index@restoreFilterStorage: Invalid stored filter.', {err})
                return false
            }

            return true
        },
        /**
         * Clears the advanced filter, keeping the status tab
         */
        clearSearchFilters() {
            this.$set(this.filter, 'advanced', [])
        },
        /**
         * DataTable
         * @returns {Object}
         */
        table() {
            return {
                row: {
                    'class': "hover:bg-gray-50",
                    'checkedClass': "bg-gray-100",
                },
                cols: {
                    id: {
                        'th': 'ID',
                        sort: true
                    },
                    shipment: {
                        th: 'Remessa'
                    },
                    subject: {
                        th: 'Assunto',
                        thClass: 'w-full',
                        tdClass: 'group-hover:text-blue-600'
                    },
                    status: {
                        th: this.$t('Status'),
                        thClass: 'text-center'
                    },
                    last_message_at: {
                        th: 'Mensagem',
                        thClass: 'hidden md:table-cell',
                        tdClass: 'hidden md:table-cell',
                        formater: 'date:DD/MM/YYYY HH:mm:ss',
                        sort: true
                    },
                    created_at: {
                        th: 'Criado em',
                        thClass: 'hidden md:table-cell',
                        tdClass: 'hidden md:table-cell',
                        formater: 'date:DD/MM/YYYY HH:mm:ss',
                        sort: true
                    }
                }
            }
        },
        /**
         * INIT
         */
        async init() {

            // A aba "Todos" é status null, entao o default so vale quando nao ha filtro na URL
            const hasUrlFilter = Object.keys(this.$route.query).length > 0

            if(!hasUrlFilter && this.filter.status === null) {
                this.filter.status = -1
            }

            this.restoreFilterFromUrl()
            this.restoreFilterStorage()

            await this.load()

            document.title = 'Tristar - ' + this.$t('support_list.title')
        }
    }
}
